import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import OnboardingStepRecentProjectUi from '../ui/screens/onboarding-step-recent-project.ui';
import { RootStoreContext } from '../../core/root-store';
import { TAGS_CONFIGS, getProjectCategories, getProjectImages } from '../onboarding.service';
import { UserRole } from '../../user/user.type';

const projectCategoryTagsConfig = TAGS_CONFIGS.PROJECT_CATEGORIES;

const OnboardingStepRecentProjectContainer = observer(() => {
  const rootStore = useContext(RootStoreContext);
  const store = rootStore.onboardingStore;
  const { role } = store.formData;
  const stepErrors = store.shouldShowStepErrors;

  const [projectPrefabImages, setProjectPrefabImages] = useState<string[]>([]);
  const [projectCategoryTags, setProjectCategoryTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [prefabImagesLoading, setPrefabImagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjectCategoryTags = async () => {
      if (role === UserRole.Founder) {
        try {
          setLoading(true);
          const pcTags = await getProjectCategories();
          setProjectCategoryTags(pcTags);
        } catch (error) {
          const errorMessage = 'Failed to load options';
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      }
      return;
    };

    const fetchProjectPrefabImages = async () => {
      try {
        setPrefabImagesLoading(true);
        const images = await getProjectImages();
        setProjectPrefabImages(images);
      } catch (error) {
        const errorMessage = 'Failed to load project images';
        setError(errorMessage);
      } finally {
        setPrefabImagesLoading(false);
      }
    };

    fetchProjectCategoryTags();
    fetchProjectPrefabImages();
  }, [role]);

  const getCurrentImage = (imageType: 'profile' | 'project'): string => {
    const selectedFile = imageType === 'profile'
      ? store.documents.profileImage.selectedFile
      : store.documents.projectImage.selectedFile;

    if (selectedFile) {
      return URL.createObjectURL(selectedFile);
    }

    return imageType === 'profile'
      ? store.formData.profileImageUrl
      : store.formData.recentProject.projectImage;
  };

  const updateProjectArrayField = (field: 'techStack' | 'developerTechStack' | 'projectCategory', action: 'add' | 'remove', value: string) => {
    store.updateProjectArrayField(field, action, value);
  };

  const handleProjectImageError = (error: string | null) => {
    store.setFileUploadError('projectImage', error);
  };

  return (
    <OnboardingStepRecentProjectUi
      projectName={store.formData.recentProject.name}
      projectDescription={store.formData.recentProject.description}
      role={role!}
      techStack={store.formData.recentProject.techStack}
      developerTechStack={store.formData.recentProject.developerTechStack}
      projectCategory={store.formData.recentProject.projectCategory}
      projectCategoryTags={projectCategoryTags}
      projectCategoryTagsLoading={loading}
      fetchErrorProjectCategory={error}
      projectCategoriesMaxSelection={projectCategoryTagsConfig.maxSelection}
      projectImagePreview={getCurrentImage('project')}
      projectPrefabImages={projectPrefabImages}
      projectPrefabImagesLoading={prefabImagesLoading}
      isUploadingProjectImage={store.documents.projectImage.isUploading}

      projectImageError={stepErrors.recentProject?.projectImage}
      projectNameError={stepErrors.recentProject?.name}
      projectDescriptionError={stepErrors.recentProject?.description}
      techStackError={stepErrors.recentProject?.techStack}
      developerTechStackError={stepErrors.recentProject?.developerTechStack}
      projectCategoryError={stepErrors.recentProject?.projectCategory}

      onProjectImageSelect={(file) => store.setSelectedFile('projectImage', file)}
      onProjectImageError={handleProjectImageError}
      onUpdateProjectField={(field, value) => store.updateProjectField(field, value)}
      onAddTechStackSkill={(skill) => updateProjectArrayField('techStack', 'add', skill)}
      onRemoveTechStackSkill={(skill) => updateProjectArrayField('techStack', 'remove', skill)}
      onAddDeveloperTechStackSkill={(skill) => updateProjectArrayField('developerTechStack', 'add', skill)}
      onRemoveDeveloperTechStackSkill={(skill) => updateProjectArrayField('developerTechStack', 'remove', skill)}
      onAddProjectCategory={(category) => updateProjectArrayField('projectCategory', 'add', category)}
      onRemoveProjectCategory={(category) => updateProjectArrayField('projectCategory', 'remove', category)}
      onPrefabImageSelect={(imageUrl) => store.setSelectedPrefabImage(imageUrl)}
      isSubmitting={store.isSavingStep}
      projectDescriptionCount={store.projectDescriptionCount}
    />
  );
});

export default OnboardingStepRecentProjectContainer;
